/**
 * Invoice / receipt PDFs — a branded, printable HTML document per bill.
 * PAID bills render as a RECEIPT (payment method, reference, date, PAID
 * stamp); unpaid ones as an INVOICE (due date + how-to-pay from the
 * society's bank details). One template, fixed light "paper" palette like
 * the gate-pass ticket — a printed artifact doesn't follow the app theme.
 *
 * Delivery: web → hidden popup + window.print() (users pick "Save as PDF");
 * native → expo-print's OS print/PDF dialog, LAZY-imported with a capability
 * check so this file is OTA-safe on binaries that don't bake expo-print yet
 * (it ships in build #3).
 */
import { Platform } from 'react-native';

import { formatINR, palette } from '@bero/tokens';
import { invoiceCode, monthYear } from './bits';
import type { Bill, PayTo } from './api';

const P = palette.light; // printed paper — fixed light, like the ticket

export type InvoiceContext = {
  societyName?: string | null;
  residentName?: string | null;
  flatCode?: string | null;
  payTo?: PayTo | null;
};

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function day(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function rupees(paise: number): string {
  return formatINR(paise / 100, { decimals: paise % 100 !== 0 ? 2 : 0 });
}

/** The bero arch mark as inline SVG — no external assets in a print doc. */
function markSvg(size: number): string {
  const u = size / 58;
  return `<svg width="${50 * u}" height="${size}" viewBox="0 0 50 58" xmlns="http://www.w3.org/2000/svg">
    <path d="M 7 58 L 7 24 A 18 18 0 0 1 43 24 L 43 58" fill="none" stroke="${P.primary500}" stroke-width="9"/>
    <circle cx="25" cy="30" r="6" fill="${P.accent500}"/>
    <rect x="20" y="40" width="10" height="16" rx="5" fill="${P.accent500}"/>
  </svg>`;
}

export function invoiceHtml(bill: Bill, ctx: InvoiceContext): string {
  const paid = bill.status === 'PAID';
  const pay = bill.payments[0];
  const code = invoiceCode(bill.period, bill.id);
  const kind = paid ? 'RECEIPT' : 'INVOICE';
  const lines = bill.breakdown.map((l) => `
      <tr><td>${esc(l.label)}</td><td class="num">${rupees(l.amount)}</td></tr>`).join('');
  const payToRows = !paid && ctx.payTo ? [
    ctx.payTo.upiVpa ? `<div class="payrow"><span>UPI</span><b>${esc(ctx.payTo.upiVpa)}</b></div>` : '',
    ctx.payTo.bankName ? `<div class="payrow"><span>Bank</span><b>${esc(ctx.payTo.bankName)}</b></div>` : '',
    ctx.payTo.bankAccount ? `<div class="payrow"><span>Account</span><b>${esc(ctx.payTo.bankAccount)}</b></div>` : '',
    ctx.payTo.bankIfsc ? `<div class="payrow"><span>IFSC</span><b>${esc(ctx.payTo.bankIfsc)}</b></div>` : '',
  ].join('') : '';

  return `<!doctype html>
<html><head><meta charset="utf-8">
<title>${kind} ${esc(code)}</title>
<style>
  @page { size: A4; margin: 18mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { background: #fff; }
  body {
    font-family: -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    color: ${P.textPrimary}; font-size: 13px; line-height: 1.45;
  }
  .sheet { max-width: 640px; margin: 0 auto; padding: 40px 8px; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; }
  .brand { display: flex; align-items: center; gap: 10px; }
  .brand b { font-size: 21px; letter-spacing: -0.8px; }
  .kind { text-align: right; }
  .kind .k { font-size: 11px; letter-spacing: 2.5px; color: ${P.primary500}; font-weight: 600; }
  .kind .code { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size: 13px; margin-top: 4px; color: ${P.textSecondary}; }
  .society { margin-top: 6px; color: ${P.textSecondary}; }
  .rule { border: 0; border-top: 1px solid ${P.borderSoft}; margin: 22px 0; }
  .meta { display: flex; justify-content: space-between; gap: 24px; }
  .meta .lbl { font-size: 10px; letter-spacing: 1.6px; color: ${P.textTertiary}; text-transform: uppercase; margin-bottom: 5px; }
  .meta .col div + div { margin-top: 2px; }
  .meta .right { text-align: right; }
  table { width: 100%; border-collapse: collapse; margin-top: 26px; }
  th { font-size: 10px; letter-spacing: 1.6px; color: ${P.textTertiary}; text-transform: uppercase; text-align: left; font-weight: 500; padding: 0 0 8px; border-bottom: 1px solid ${P.border}; }
  th.num, td.num { text-align: right; font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; }
  td { padding: 9px 0; border-bottom: 1px solid ${P.borderSoft}; }
  tr.total td { border-bottom: 0; border-top: 2px solid ${P.textPrimary}; font-weight: 700; font-size: 15px; padding-top: 12px; }
  .stamp-row { display: flex; justify-content: space-between; align-items: center; margin-top: 30px; gap: 24px; }
  .stamp {
    display: inline-block; padding: 8px 22px; border: 2.5px solid ${paid ? P.success500 : P.warning500};
    color: ${paid ? P.success700 : P.warning700}; border-radius: 8px;
    font-weight: 800; font-size: 17px; letter-spacing: 4px; transform: rotate(-4deg);
  }
  .paydetail { font-size: 12px; color: ${P.textSecondary}; text-align: right; }
  .paydetail b { color: ${P.textPrimary}; }
  .howto { margin-top: 28px; background: ${P.canvas1}; border-radius: 10px; padding: 16px 18px; }
  .howto .h { font-size: 10px; letter-spacing: 1.6px; color: ${P.textTertiary}; text-transform: uppercase; margin-bottom: 8px; }
  .payrow { display: flex; gap: 14px; padding: 2px 0; }
  .payrow span { width: 70px; color: ${P.textTertiary}; }
  .payrow b { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-weight: 600; }
  .foot { margin-top: 40px; padding-top: 14px; border-top: 1px solid ${P.borderSoft}; display: flex; justify-content: space-between; font-size: 11px; color: ${P.textTertiary}; }
</style></head>
<body><div class="sheet">
  <div class="top">
    <div class="brand">${markSvg(30)}<b>bero</b></div>
    <div class="kind"><div class="k">${kind}</div><div class="code">${esc(code)}</div></div>
  </div>
  <div class="society">${esc(ctx.societyName ?? 'Housing society')}</div>
  <hr class="rule"/>
  <div class="meta">
    <div class="col">
      <div class="lbl">Billed to</div>
      <div><b>${esc(ctx.residentName ?? 'Resident')}</b></div>
      <div>${esc(ctx.flatCode ? `Flat ${ctx.flatCode}` : '')}</div>
    </div>
    <div class="col right">
      <div class="lbl">Period</div>
      <div><b>${esc(monthYear(bill.period))}</b></div>
      <div>${paid && pay ? `Paid ${day(pay.paidAt)}` : `Due ${day(bill.dueDate)}`}</div>
    </div>
  </div>
  <table>
    <thead><tr><th>Description</th><th class="num">Amount</th></tr></thead>
    <tbody>
      ${lines}
      ${bill.lateFee > 0 ? `<tr><td>Late fee</td><td class="num">${rupees(bill.lateFee)}</td></tr>` : ''}
      <tr class="total"><td>Total</td><td class="num">${rupees(bill.totalAmount)}</td></tr>
    </tbody>
  </table>
  <div class="stamp-row">
    <span class="stamp">${paid ? 'PAID' : bill.status === 'OVERDUE' ? 'OVERDUE' : 'DUE'}</span>
    <div class="paydetail">${paid && pay
      ? `Received <b>${rupees(pay.amount)}</b> via <b>${esc(pay.method)}</b><br/>${pay.reference ? `Ref ${esc(pay.reference)} · ` : ''}${day(pay.paidAt)}`
      : `Payable by ${day(bill.dueDate)}`}</div>
  </div>
  ${payToRows ? `<div class="howto"><div class="h">How to pay</div>${payToRows}</div>` : ''}
  <div class="foot">
    <span>Generated by Bero · usebero.com</span>
    <span>${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
  </div>
</div></body></html>`;
}

/**
 * Open the OS print/PDF flow for an invoice. Returns false when the current
 * native binary can't print yet (pre-build-#3 install) — caller shows a note.
 */
export async function printInvoice(html: string): Promise<boolean> {
  if (Platform.OS === 'web') {
    if (typeof window === 'undefined') return false;
    // Blob URL instead of document.write — same-origin, so we can still
    // trigger the print dialog once the document has laid out.
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const w = window.open(url, '_blank');
    if (!w) { URL.revokeObjectURL(url); return false; }
    w.addEventListener('load', () => {
      setTimeout(() => { w.focus(); w.print(); URL.revokeObjectURL(url); }, 150);
    });
    return true;
  }
  try {
    const Print = await import('expo-print');
    if (typeof Print.printAsync !== 'function') return false;
    await Print.printAsync({ html });
    return true;
  } catch {
    return false;
  }
}
